import React from 'react';
import { MapPinIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { VENUE_TYPES, VIRTUAL_PLATFORMS, COUNTRIES } from '../../../data/eventSchema';
import type { EM } from './useEventManager';
export const LocationSection: React.FC<{
  em: EM;
}> = ({
  em
}) => {
  const dk = useDarkClasses();
  const {
    formData,
    updateField,
    updateNestedField
  } = em;
  // Helper to determine what to show
  const showInPerson = !formData.isVirtual || formData.isVirtual === undefined; // Default to in-person
  const showVirtual = formData.isVirtual;
  return <div className={`p-6 rounded-xl shadow-sm ${dk.card}`} data-id="element-19299" data-mp="36zy1q1voruie">
      <h2 className={`text-lg font-bold mb-4 flex items-center ${dk.textHeading}`} data-id="element-19300" data-mp="372j4qu7q0ghq">
        <MapPinIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-19301" data-mp="3c1ncdgezy65y" />
        Location
      </h2>

      <div className="mb-6" data-id="element-19302" data-mp="3h0gs7fhra99a">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-19303" data-mp="3lxqi0w924mxn">
          Event Format
        </label>
        <div className="flex gap-4" data-id="element-19304" data-mp="hd3b9ue0xxrs">
          <label className="flex items-center" data-id="element-19305" data-mp="he4tqjxddmjj">
            <input type="radio" checked={!formData.isVirtual} onChange={() => updateField('isVirtual', false)} className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500" data-id="element-19306" data-mp="cdqpze2swyrb" />
            <span className={`ml-2 text-sm ${dk.textPrimary}`} data-id="element-19307" data-mp="3elnjwo4cl9v">In-Person</span>
          </label>
          <label className="flex items-center" data-id="element-19308" data-mp="1029vjluguxfp">
            <input type="radio" checked={formData.isVirtual} onChange={() => updateField('isVirtual', true)} className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500" data-id="element-19309" data-mp="v3gghozj6ydf" />
            <span className={`ml-2 text-sm ${dk.textPrimary}`} data-id="element-19310" data-mp="mbejk0oo807w">Virtual</span>
          </label>
        </div>
      </div>

      <div className="space-y-6" data-id="element-19311" data-mp="1ivdtvkimpygn">
        {showInPerson && <div className="space-y-4" data-id="element-19312" data-mp="1e19jsse2habc">
            <h3 className={`text-sm font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-19313" data-mp="38aogrhdi4f6a">
              In-Person Details
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-19314" data-mp="3d8zptpvz2lng">
              <div data-id="element-19315" data-mp="2yjtuid099fku">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19316" data-mp="2ykbxdshfas1q">
                  Venue Name <span className="text-red-500" data-id="element-19317" data-mp="2pk6r5lnytzp5">*</span>
                </label>
                <input type="text" value={formData.location?.venueName || ''} onChange={e => updateNestedField('location', 'venueName', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="e.g. The Strand Bookstore" data-id="element-19318" data-mp="2pm9q4v73j360" />
              </div>
              <div data-id="element-19319" data-mp="itqrc4zqf1yu">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19320" data-mp="iu8xbtc74ujy">
                  Venue Type
                </label>
                <select value={formData.location?.venueType || ''} onChange={e => updateNestedField('location', 'venueType', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-19321" data-mp="1ycorxp1e273r">
                  <option value="" data-id="element-19322" data-mp="2m1q2bhts7sbo">Select Venue Type</option>
                  {VENUE_TYPES.map(type => <option key={type.value} value={type.value} data-id="element-19323" data-mp="2i1fkn7wmndgz">
                      {type.label}
                    </option>)}
                </select>
              </div>
            </div>

            <div data-id="element-19324" data-mp="2r2dui6tqyf7h">
              <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19325" data-mp="2r1vm6btt8aj7">
                Street Address
              </label>
              <input type="text" value={formData.location?.streetAddress || ''} onChange={e => updateNestedField('location', 'streetAddress', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="123 Main St" data-id="element-19326" data-mp="wo4j5v5zkowq" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-19327" data-mp="1d20yrmr5i36l">
              <div className="col-span-2 sm:col-span-1" data-id="element-19328" data-mp="fe0mcu8hzkkn">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19329" data-mp="ffk30ly2fxmy">
                  City <span className="text-red-500" data-id="element-19330" data-mp="kaqy55d9ru09">*</span>
                </label>
                <input type="text" value={formData.location?.city || ''} onChange={e => updateNestedField('location', 'city', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-19331" data-mp="pavij6hat8vr" />
              </div>
              <div className="col-span-2 sm:col-span-1" data-id="element-19332" data-mp="2ex5bhfz9u3i9">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19333" data-mp="2euj4bhgalq0k">
                  State/Region
                </label>
                <input type="text" value={formData.location?.region || ''} onChange={e => updateNestedField('location', 'region', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-19334" data-mp="25uci976j3hps" />
              </div>
              <div className="col-span-2 sm:col-span-1" data-id="element-19335" data-mp="1n564lykldgau">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19336" data-mp="1n3la28ya1xcz">
                  Postcode
                </label>
                <input type="text" value={formData.location?.postcode || ''} onChange={e => updateNestedField('location', 'postcode', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-19337" data-mp="1d708xypdp6ef" />
              </div>
              <div className="col-span-2 sm:col-span-1" data-id="element-19338" data-mp="pkmr14acu1f7">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19339" data-mp="pm7q977m68je">
                  Country <span className="text-red-500" data-id="element-19340" data-mp="1hasncvbavk9c">*</span>
                </label>
                <select value={formData.location?.country || ''} onChange={e => updateNestedField('location', 'country', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-19341" data-mp="1madys57npl2y">
                  <option value="" data-id="element-19342" data-mp="20aexpf4d2ifs">Select Country</option>
                  {COUNTRIES.map(country => <option key={country} value={country} data-id="element-19343" data-mp="251excs4v7elz">
                      {country}
                    </option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-19344" data-mp="3ksqwpb65xofo">
              <div data-id="element-19345" data-mp="3kqd0gyzqy66j">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19346" data-mp="3bfyxamou58c">
                  Room / Table Number
                </label>
                <input type="text" value={formData.location?.roomOrTable || ''} onChange={e => updateNestedField('location', 'roomOrTable', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="e.g. Hall A, Table 42" data-id="element-19347" data-mp="36sg525x145tg" />
              </div>
              <div data-id="element-19348" data-mp="vzvbzrm9edh1">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19349" data-mp="vz24gofh7jxu">
                  Map Link
                </label>
                <input type="url" value={formData.location?.mapLink || ''} onChange={e => updateNestedField('location', 'mapLink', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Google Maps URL" data-id="element-19350" data-mp="2kkj046eofxeg" />
              </div>
            </div>
          </div>}

        {showVirtual && <div className="space-y-4" data-id="element-19351" data-mp="389lft8pr3g6b">
            <h3 className={`text-sm font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-19352" data-mp="388k73aw7j5rr">
              Virtual Details
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-19353" data-mp="3i7j2vsvvj6xc">
              <div data-id="element-19354" data-mp="3i5osv9m54z81">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19355" data-mp="1iung5rni256a">
                  Platform <span className="text-red-500" data-id="element-19356" data-mp="1nut9rddby1r0">*</span>
                </label>
                <select value={formData.virtualDetails?.platform || ''} onChange={e => updateNestedField('virtualDetails', 'platform', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-19357" data-mp="1gar2z9rr8pwh">
                  <option value="" data-id="element-19358" data-mp="1z9d1cgvw02wp">Select Platform</option>
                  {VIRTUAL_PLATFORMS.map(platform => <option key={platform.value} value={platform.value} data-id="element-19359" data-mp="1ubbinx2di5lr">
                      {platform.label}
                    </option>)}
                </select>
              </div>
              <div data-id="element-19360" data-mp="1kbay67srpjfz">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19361" data-mp="1kdx46300fqw3">
                  Access Link
                </label>
                <input type="url" value={formData.virtualDetails?.accessLink || ''} onChange={e => updateNestedField('virtualDetails', 'accessLink', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="https://..." data-id="element-19362" data-mp="8v47jyqiv3x8" />
              </div>
            </div>

            <div className="flex items-center" data-id="element-19363" data-mp="3usrsfozitz4r">
              <input type="checkbox" id="replayAvailable" checked={formData.virtualDetails?.replayAvailable || false} onChange={e => updateNestedField('virtualDetails', 'replayAvailable', e.target.checked)} className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500" data-id="element-19364" data-mp="3qoaazwgmvxx5" />
              <label htmlFor="replayAvailable" className={`ml-2 text-sm font-medium ${dk.textPrimary}`} data-id="element-19365" data-mp="kuzoyquqpvlb">
                Replay will be available after the event
              </label>
            </div>
          </div>}
      </div>
    </div>;
};